/**
 * usePlaybackCommand Hook - 2ECOMP Fleet Management
 *
 * Manages the full JT1078 playback flow:
 * 1. Query video resources on SD card (0x9205)
 * 2. Wait for resource list response (0x1205 via WebSocket + API poll fallback)
 * 3. Request playback (0x9201) — player renders immediately via WS-FLV
 * 4. Control playback (0x9202): pause, resume, speed, seek, stop
 */

import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import {
  useVideoCommand,
  PLAYBACK_CONTROL,
  SPEED_MULTIPLIER,
} from './useVideoCommand';

/**
 * Playback states
 */
export const PLAYBACK_STATE = {
  IDLE: 'idle',
  QUERYING: 'querying',
  RESOURCES_LOADED: 'resources_loaded',
  REQUESTING: 'requesting',
  WAITING_STREAM: 'waiting_stream',
  PLAYING: 'playing',
  PAUSED: 'paused',
  STOPPED: 'stopped',
  ERROR: 'error',
};

const RESOURCE_RESPONSE_TIMEOUT = 30000;
const RESOURCE_POLL_INTERVAL = 2000;

/**
 * Parse BCD time string (from decoder) to Date
 * Format: "YYMMDDHHmmss" -> Date
 */
const parseBcdTime = (bcd) => {
  if (!bcd || bcd.length < 12) return null;
  const year = 2000 + parseInt(bcd.substring(0, 2), 10);
  const month = parseInt(bcd.substring(2, 4), 10) - 1;
  const day = parseInt(bcd.substring(4, 6), 10);
  const hour = parseInt(bcd.substring(6, 8), 10);
  const min = parseInt(bcd.substring(8, 10), 10);
  const sec = parseInt(bcd.substring(10, 12), 10);
  return new Date(year, month, day, hour, min, sec);
};

/**
 * Parse video resources string from backend
 * Format: "ch1:260205100000-260205101500;ch1:260205101500-260205103000;"
 */
const parseVideoResources = (resourceStr, resourceCount) => {
  if (!resourceStr) return [];

  const resources = [];
  const entries = resourceStr.split(';').filter(Boolean);

  for (const entry of entries) {
    const match = entry.match(/^ch(\d+):(\w+)-(\w+)$/);
    if (match) {
      const channel = parseInt(match[1], 10);
      const startTime = parseBcdTime(match[2]);
      const endTime = parseBcdTime(match[3]);

      if (startTime && endTime) {
        resources.push({
          channel,
          startTime,
          endTime,
          startTimeStr: match[2],
          endTimeStr: match[3],
          duration: Math.round((endTime - startTime) / 1000),
        });
      }
    }
  }

  return resources;
};

/**
 * Format Date to ISO-like string for backend: "YYYY-MM-DDTHH:mm:ss"
 */
const formatTimeForCommand = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Hook for managing JT1078 playback flow
 */
export const usePlaybackCommand = (deviceId) => {
  const position = useSelector((state) => state.session.positions[deviceId]);

  const {
    queryVideoResources,
    requestPlayback,
    controlPlayback: sendControlPlayback,
    stopPlayback: sendStopPlayback,
  } = useVideoCommand();

  // State
  const [state, setState] = useState(PLAYBACK_STATE.IDLE);
  const [resources, setResources] = useState([]);
  const [currentSpeed, setCurrentSpeed] = useState(SPEED_MULTIPLIER.X1);
  const [activeChannel, setActiveChannel] = useState(1);
  const [error, setError] = useState(null);

  // Refs
  const mountedRef = useRef(true);
  const responseTimeoutRef = useRef(null);
  const resourcePollRef = useRef(null);
  const waitingForResourcesRef = useRef(false);

  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    if (resourcePollRef.current) {
      clearInterval(resourcePollRef.current);
      resourcePollRef.current = null;
    }
  }, []);

  /**
   * Process a videoResourceList response (from WebSocket or API poll)
   */
  const handleResourceResponse = useCallback((attrs) => {
    if (!waitingForResourcesRef.current) return;
    waitingForResourcesRef.current = false;
    clearTimers();

    const parsed = parseVideoResources(
      attrs.videoResources,
      attrs.resourceCount,
    );

    setResources(parsed);
    setState(parsed.length > 0
      ? PLAYBACK_STATE.RESOURCES_LOADED
      : PLAYBACK_STATE.IDLE);

    if (parsed.length === 0) {
      setError('Nenhuma gravacao encontrada no periodo selecionado');
    }
  }, [clearTimers]);

  /**
   * Monitor position updates via WebSocket for video resource list (0x1205)
   */
  useEffect(() => {
    if (!position || !waitingForResourcesRef.current) return;
    const attrs = position.attributes;
    if (attrs?.result === 'videoResourceList') {
      handleResourceResponse(attrs);
    }
  }, [position, handleResourceResponse]);

  /**
   * Query video resources on device SD card
   * Uses dual detection: WebSocket position monitor + API polling fallback
   */
  const queryResources = useCallback(async (channel, startTime, endTime) => {
    if (!deviceId) return;

    clearTimers();
    setError(null);
    setState(PLAYBACK_STATE.QUERYING);
    setResources([]);
    setActiveChannel(channel);
    waitingForResourcesRef.current = true;

    try {
      await queryVideoResources(deviceId, {
        channel,
        startTime: formatTimeForCommand(startTime),
        endTime: formatTimeForCommand(endTime),
      });

      // API polling fallback: periodically check latest position for videoResourceList
      // Works even when camera is connected to a different server (e.g., production)
      resourcePollRef.current = setInterval(async () => {
        if (!mountedRef.current || !waitingForResourcesRef.current) {
          if (resourcePollRef.current) {
            clearInterval(resourcePollRef.current);
            resourcePollRef.current = null;
          }
          return;
        }
        try {
          const resp = await fetch(`/api/positions?deviceId=${deviceId}`, {
            credentials: 'include',
          });
          if (!resp.ok) return;
          const positions = await resp.json();
          const pos = positions?.[0];
          if (pos?.attributes?.result === 'videoResourceList') {
            handleResourceResponse(pos.attributes);
          }
        } catch {
          // Ignore polling errors
        }
      }, RESOURCE_POLL_INTERVAL);

      // Set timeout for response
      responseTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && waitingForResourcesRef.current) {
          waitingForResourcesRef.current = false;
          clearTimers();
          setState(PLAYBACK_STATE.ERROR);
          setError('Tempo esgotado aguardando resposta do dispositivo');
        }
      }, RESOURCE_RESPONSE_TIMEOUT);
    } catch (err) {
      console.error('[Playback] Error querying resources:', err);
      waitingForResourcesRef.current = false;
      setState(PLAYBACK_STATE.ERROR);
      setError(`Erro ao consultar gravacoes: ${err.message}`);
    }
  }, [deviceId, queryVideoResources, clearTimers, handleResourceResponse]);

  /**
   * Start playback of a recording.
   * Stops any existing playback first, then requests new playback (0x9201).
   * Sets PLAYING immediately so the player renders and connects to ZLM via
   * WS-FLV — the connection itself triggers ZLM's on_stream_not_found webhook,
   * which starts the FFmpeg bridge from M7S. No API polling needed.
   */
  const startPlayback = useCallback(async (channel, startTime, endTime) => {
    if (!deviceId) return;

    clearTimers();
    setError(null);
    setState(PLAYBACK_STATE.REQUESTING);
    setActiveChannel(channel);
    setCurrentSpeed(SPEED_MULTIPLIER.X1);

    try {
      // Stop previous playback on the device (0x9202 END) before requesting new
      try {
        await sendStopPlayback(deviceId, { channel });
      } catch (e) {
        // Ignore — device may not have active playback
      }

      if (!mountedRef.current) return;

      // Request new playback (0x9201)
      await requestPlayback(deviceId, {
        channel,
        startTime: formatTimeForCommand(startTime),
        endTime: formatTimeForCommand(endTime),
        speedMultiplier: SPEED_MULTIPLIER.X1,
      });

      // Set PLAYING immediately — the player will connect to ZLM via WS-FLV,
      // triggering the on_stream_not_found webhook that starts the FFmpeg bridge.
      // The player's built-in reconnection handles any startup delay.
      setState(PLAYBACK_STATE.PLAYING);
    } catch (err) {
      console.error('[Playback] Error requesting playback:', err);
      setState(PLAYBACK_STATE.ERROR);
      setError(`Erro ao solicitar playback: ${err.message}`);
    }
  }, [deviceId, requestPlayback, sendStopPlayback, clearTimers]);

  /**
   * Pause playback
   */
  const pausePlayback = useCallback(async (channel) => {
    const ch = channel || activeChannel;
    try {
      await sendControlPlayback(deviceId, {
        channel: ch,
        playbackControl: PLAYBACK_CONTROL.PAUSE,
      });
      setState(PLAYBACK_STATE.PAUSED);
    } catch (err) {
      console.error('[Playback] Error pausing:', err);
    }
  }, [deviceId, activeChannel, sendControlPlayback]);

  /**
   * Resume playback
   */
  const resumePlayback = useCallback(async (channel) => {
    const ch = channel || activeChannel;
    try {
      await sendControlPlayback(deviceId, {
        channel: ch,
        playbackControl: PLAYBACK_CONTROL.START,
        speedMultiplier: currentSpeed,
      });
      setState(PLAYBACK_STATE.PLAYING);
    } catch (err) {
      console.error('[Playback] Error resuming:', err);
    }
  }, [deviceId, activeChannel, currentSpeed, sendControlPlayback]);

  /**
   * Seek to specific position
   */
  const seekPlayback = useCallback(async (channel, timestamp) => {
    const ch = channel || activeChannel;
    try {
      await sendControlPlayback(deviceId, {
        channel: ch,
        playbackControl: PLAYBACK_CONTROL.DRAG,
        dragPosition: formatTimeForCommand(timestamp),
      });
      setState(PLAYBACK_STATE.PLAYING);
    } catch (err) {
      console.error('[Playback] Error seeking:', err);
    }
  }, [deviceId, activeChannel, sendControlPlayback]);

  /**
   * Change playback speed
   */
  const changeSpeed = useCallback(async (channel, speed) => {
    const ch = channel || activeChannel;
    setCurrentSpeed(speed);
    try {
      await sendControlPlayback(deviceId, {
        channel: ch,
        playbackControl: PLAYBACK_CONTROL.FAST_FORWARD,
        speedMultiplier: speed,
      });
    } catch (err) {
      console.error('[Playback] Error changing speed:', err);
    }
  }, [deviceId, activeChannel, sendControlPlayback]);

  /**
   * Stop playback
   */
  const stopPlayback = useCallback(async (channel) => {
    const ch = channel || activeChannel;
    clearTimers();
    try {
      await sendStopPlayback(deviceId, { channel: ch });
    } catch (err) {
      console.error('[Playback] Error stopping:', err);
    }
    setState(PLAYBACK_STATE.STOPPED);
    setCurrentSpeed(SPEED_MULTIPLIER.X1);
  }, [deviceId, activeChannel, sendStopPlayback, clearTimers]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    clearTimers();
    waitingForResourcesRef.current = false;
    setState(PLAYBACK_STATE.IDLE);
    setResources([]);
    setError(null);
    setCurrentSpeed(SPEED_MULTIPLIER.X1);
  }, [clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
      waitingForResourcesRef.current = false;
    };
  }, [clearTimers]);

  return {
    // State
    state,
    resources,
    currentSpeed,
    activeChannel,
    error,
    isLoading: state === PLAYBACK_STATE.QUERYING
      || state === PLAYBACK_STATE.REQUESTING
      || state === PLAYBACK_STATE.WAITING_STREAM,
    isPlaying: state === PLAYBACK_STATE.PLAYING,
    isPaused: state === PLAYBACK_STATE.PAUSED,

    // Actions
    queryResources,
    startPlayback,
    pausePlayback,
    resumePlayback,
    seekPlayback,
    changeSpeed,
    stopPlayback,
    reset,
  };
};

export default usePlaybackCommand;
